/**
 * Export Service
 * Handles various export formats for reports (PDF, CSV, Excel, Print)
 */

import { REPORT_FORMATS } from '../../reports.generators/types/reportTypes';
import { 
  createReportBlob, 
  downloadReport, 
  generateReportFilename,
  getMimeType 
} from '../../reports.generators/utils/reportUtils';

class ExportService {
  constructor() {
    this.exportHistory = [];
    this.maxHistorySize = 50;
  }

  /**
   * Export report in specified format
   */
  async exportReport(content, reportType, parameters = {}, options = {}) {
    const {
      format = REPORT_FORMATS.PDF,
      filename = null,
      download = true,
      openInNewWindow = false
    } = options;

    try {
      let exportedContent;
      let exportResult;

      switch (format) {
        case REPORT_FORMATS.PDF:
          exportResult = await this.exportToPDF(content, reportType, parameters, options);
          break;
        case REPORT_FORMATS.CSV:
          exportResult = await this.exportToCSV(content, reportType, parameters, options);
          break;
        case REPORT_FORMATS.EXCEL:
          exportResult = await this.exportToExcel(content, reportType, parameters, options);
          break;
        case REPORT_FORMATS.HTML:
          exportResult = await this.exportToHTML(content, reportType, parameters, options);
          break;
        case REPORT_FORMATS.JSON:
          exportResult = await this.exportToJSON(content, reportType, parameters, options);
          break;
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      // Add to history
      this.addToHistory({
        reportType,
        format,
        filename: exportResult.filename,
        timestamp: new Date(),
        success: true
      });

      // Handle download or opening
      if (download && exportResult.blob) {
        this.downloadBlob(exportResult.blob, exportResult.filename);
      }

      if (openInNewWindow && format === REPORT_FORMATS.HTML) {
        this.openHTMLInNewWindow(exportResult.content);
      }

      return exportResult;
    } catch (error) {
      console.error('Export failed:', error);
      
      // Add failed export to history
      this.addToHistory({
        reportType,
        format,
        timestamp: new Date(),
        success: false,
        error: error.message
      });

      throw error;
    }
  }

  /**
   * Export to PDF
   */
  async exportToPDF(content, reportType, parameters, options = {}) {
    const {
      pageSize = 'A4',
      orientation = reportType === 'service-attendees' ? 'landscape' : 'portrait',
      margins = { top: 20, right: 20, bottom: 20, left: 20 }
    } = options;

    try {
      // Create a temporary window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Popup blocked - please allow popups for this site');
      }

      // Enhanced CSS for PDF/print optimization
      const printCSS = `
        <style>
          @page {
            size: ${pageSize} ${orientation};
            margin: ${margins.top}px ${margins.right}px ${margins.bottom}px ${margins.left}px;
          }

          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 10pt;
            line-height: 1.3;
            color: #000;
            margin: 0;
            padding: 0;
          }

          .header {
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }

          .header h1 {
            margin: 0 0 5px 0;
            font-size: 18pt;
            font-weight: bold;
          }

          .header h2 {
            margin: 0 0 5px 0;
            font-size: 14pt;
            color: #666;
          }

          .header p {
            margin: 2px 0;
            font-size: 9pt;
            color: #666;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
            table-layout: fixed;
          }

          .service-attendees-table {
            font-size: 8pt;
          }

          th, td {
            border: 1px solid #ddd;
            padding: 4px 2px;
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          th {
            background-color: #f5f5f5;
            font-weight: bold;
            font-size: 8pt;
          }

          .service-attendees-table th,
          .service-attendees-table td {
            font-size: 7pt;
            padding: 3px 1px;
          }

          .metadata-section {
            margin-top: 10px;
            font-size: 8pt;
            color: #666;
          }

          .no-data {
            text-align: center;
            padding: 20px;
            color: #666;
            font-style: italic;
          }

          @media print {
            body { -webkit-print-color-adjust: exact; }
            .service-attendees-table th { background-color: #f5f5f5 !important; }
          }
        </style>
      `;

      // Write content to the print window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${generateReportFilename(reportType, parameters, 'pdf').replace('.pdf', '')}</title>
            <meta charset="utf-8">
            ${printCSS}
          </head>
          <body>
            ${content}
          </body>
        </html>
      `);

      printWindow.document.close();

      // Wait for content to load, then show options
      printWindow.onload = () => {
        // Add control buttons to the page
        const controlsHTML = `
          <div style="position: fixed; top: 10px; right: 10px; z-index: 9999; background: white; padding: 10px; border: 2px solid #333; border-radius: 5px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
            <button onclick="window.print()" style="margin-right: 10px; padding: 8px 16px; background: #007cba; color: white; border: none; border-radius: 3px; cursor: pointer;">Save as PDF</button>
            <button onclick="window.close()" style="padding: 8px 16px; background: #666; color: white; border: none; border-radius: 3px; cursor: pointer;">Close</button>
          </div>
        `;

        printWindow.document.body.insertAdjacentHTML('afterbegin', controlsHTML);

        // Focus the window
        printWindow.focus();
      };

      return {
        type: 'pdf-preview-opened',
        filename: generateReportFilename(reportType, parameters, REPORT_FORMATS.PDF),
        success: true,
        note: 'PDF preview opened in new window - click "Save as PDF" button to download or view in browser'
      };

    } catch (error) {
      throw new Error(`PDF export failed: ${error.message}`);
    }
  }

  /**
   * Export to CSV
   */
  async exportToCSV(content, reportType, parameters, options = {}) {
    const { delimiter = ',', includeHeaders = true } = options;

    try {
      let csvContent;

      // Handle service-attendees report differently due to dynamic structure
      if (reportType === 'service-attendees' && content.data && content.columns) {
        // For service attendees report, use the structured data and dynamic columns
        const headers = content.columns.map(col => col.label);
        const rows = content.data.map(row => 
          content.columns.map(col => {
            const value = row[col.key] || '';
            // Escape quotes and handle special characters
            return typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))
              ? `"${value.replace(/"/g, '""')}"` 
              : value;
          })
        );
        
        const csvRows = includeHeaders ? [headers, ...rows] : rows;
        csvContent = csvRows.map(row => row.join(delimiter)).join('\n');
        
      } else if (typeof content === 'string' && content.includes('\n')) {
        // If content is already CSV string from generator
        csvContent = content;
      } else {
        // Convert HTML table to CSV (basic implementation)
        csvContent = this.htmlTableToCSV(content, delimiter, includeHeaders);
      }

      const filename = generateReportFilename(reportType, parameters, REPORT_FORMATS.CSV);
      const blob = createReportBlob(csvContent, REPORT_FORMATS.CSV);

      return {
        type: 'csv',
        content: csvContent,
        filename,
        blob,
        mimeType: getMimeType(REPORT_FORMATS.CSV)
      };
    } catch (error) {
      throw new Error(`CSV export failed: ${error.message}`);
    }
  }

  /**
   * Export to Excel
   */
  async exportToExcel(content, reportType, parameters, options = {}) {
    const { worksheetName = 'Report Data' } = options;

    try {
      // Dynamically import xlsx to avoid bundle size impact when not used
      const XLSX = await import('xlsx');
      
      const filename = generateReportFilename(reportType, parameters, REPORT_FORMATS.EXCEL);
      
      let workbook;
      let worksheetData;

      // Handle service-attendees report differently due to dynamic structure
      if (reportType === 'service-attendees' && content.data && content.columns) {
        // For service attendees report, use the structured data and dynamic columns
        const headers = content.columns.map(col => col.label);
        const rows = content.data.map(row => 
          content.columns.map(col => row[col.key] || '')
        );
        
        worksheetData = [headers, ...rows];
        
        // Create worksheet from array of arrays
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        
        // Auto-size columns based on content
        const columnWidths = headers.map((header, index) => {
          const maxLength = Math.max(
            header.length,
            ...rows.map(row => String(row[index] || '').length)
          );
          return { wch: Math.min(Math.max(maxLength + 2, 10), 50) }; // Min 10, max 50 chars
        });
        worksheet['!cols'] = columnWidths;
        
        // Create workbook and add worksheet
        workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);
        
        // Add summary sheet if available
        if (content.summary) {
          const summaryData = [
            ['Service Attendees Report Summary'],
            [''],
            ['Date Range', `${content.summary.startDate} to ${content.summary.endDate}`],
            ['Total Attendees', content.summary.totalAttendees],
            ['Total Services', content.summary.totalServices],
            ['Total Participants', content.summary.totalParticipants],
            ['Total Revenue', `$${content.summary.totalRevenue.toFixed(2)}`],
            [''],
            ['Payment Status Breakdown'],
            ['Confirmed', content.summary.paymentStatusBreakdown?.confirmed || 0],
            ['Pending Payment', content.summary.paymentStatusBreakdown?.pending || 0],
            ['Completed', content.summary.paymentStatusBreakdown?.completed || 0],
            ['Cancelled', content.summary.paymentStatusBreakdown?.cancelled || 0]
          ];
          
          const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
          // Style the summary sheet
          summaryWorksheet['!cols'] = [{ wch: 25 }, { wch: 20 }];
          XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');
        }
        
      } else if (typeof content === 'string' && content.includes('<table')) {
        // For HTML table content, convert table to workbook
        workbook = XLSX.utils.table_to_book(content, { sheet: worksheetName });
      } else if (Array.isArray(content)) {
        // For array data, convert to worksheet
        const worksheet = XLSX.utils.json_to_sheet(content);
        workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);
      } else {
        throw new Error('Unsupported content format for Excel export');
      }

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        compression: true 
      });
      
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      return {
        type: 'excel',
        content: workbook,
        filename,
        blob,
        success: true
      };
    } catch (error) {
      throw new Error(`Excel export failed: ${error.message}`);
    }
  }

  /**
   * Export to HTML
   */
  async exportToHTML(content, reportType, parameters, options = {}) {
    try {
      const filename = generateReportFilename(reportType, parameters, REPORT_FORMATS.HTML);
      const blob = createReportBlob(content, REPORT_FORMATS.HTML);

      return {
        type: 'html',
        content: content,
        filename,
        blob,
        mimeType: getMimeType(REPORT_FORMATS.HTML)
      };
    } catch (error) {
      throw new Error(`HTML export failed: ${error.message}`);
    }
  }

  /**
   * Export to JSON
   */
  async exportToJSON(content, reportType, parameters, options = {}) {
    const { pretty = true } = options;

    try {
      let jsonContent;
      
      if (typeof content === 'object') {
        jsonContent = pretty ? JSON.stringify(content, null, 2) : JSON.stringify(content);
      } else {
        jsonContent = content;
      }

      const filename = generateReportFilename(reportType, parameters, REPORT_FORMATS.JSON);
      const blob = createReportBlob(jsonContent, REPORT_FORMATS.JSON);

      return {
        type: 'json',
        content: jsonContent,
        filename,
        blob,
        mimeType: getMimeType(REPORT_FORMATS.JSON)
      };
    } catch (error) {
      throw new Error(`JSON export failed: ${error.message}`);
    }
  }

  /**
   * Print report
   */
  async printReport(content, reportType, options = {}) {
    const {
      printStyles = true,
      pageBreaks = true,
      headerFooter = true
    } = options;

    try {
      // Create print-optimized HTML
      const printHTML = this.preparePrintHTML(content, {
        printStyles,
        pageBreaks,
        headerFooter
      });

      // Open in new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) {
        throw new Error('Unable to open print window - popup may be blocked');
      }

      printWindow.document.write(printHTML);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          
          // Close window after printing (optional)
          setTimeout(() => {
            if (!printWindow.closed) {
              printWindow.close();
            }
          }, 1000);
        }, 100);
      };

      // Add to history
      this.addToHistory({
        reportType,
        format: 'print',
        timestamp: new Date(),
        success: true
      });

      return {
        type: 'print',
        success: true,
        printWindow
      };
    } catch (error) {
      throw new Error(`Print failed: ${error.message}`);
    }
  }

  /**
   * Batch export multiple reports
   */
  async batchExport(exportRequests) {
    const results = [];

    for (const request of exportRequests) {
      try {
        const result = await this.exportReport(
          request.content,
          request.reportType,
          request.parameters,
          request.options
        );
        
        results.push({ success: true, result, request });
      } catch (error) {
        results.push({ 
          success: false, 
          error: error.message, 
          request 
        });
      }
    }

    return results;
  }

  /**
   * Convert HTML table to CSV
   */
  htmlTableToCSV(html, delimiter = ',', includeHeaders = true) {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const table = tempDiv.querySelector('table');
    if (!table) {
      throw new Error('No table found in HTML content');
    }

    const rows = [];
    
    // Get headers
    if (includeHeaders) {
      const headerRow = table.querySelector('thead tr');
      if (headerRow) {
        const headers = Array.from(headerRow.querySelectorAll('th'))
          .map(th => this.escapeCsvField(th.textContent.trim(), delimiter));
        rows.push(headers.join(delimiter));
      }
    }

    // Get data rows
    const bodyRows = table.querySelectorAll('tbody tr');
    bodyRows.forEach(row => {
      const cells = Array.from(row.querySelectorAll('td'))
        .map(td => this.escapeCsvField(td.textContent.trim(), delimiter));
      rows.push(cells.join(delimiter));
    });

    return rows.join('\n');
  }

  /**
   * Escape CSV field
   */
  escapeCsvField(field, delimiter) {
    if (field.includes(delimiter) || field.includes('"') || field.includes('\n')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  }

  /**
   * Prepare HTML for printing
   */
  preparePrintHTML(content, options = {}) {
    const { printStyles = true, pageBreaks = true, headerFooter = true } = options;

    const printCSS = printStyles ? `
      <style>
        @media print {
          @page {
            margin: 0.5in;
            size: landscape;
          }
          
          body {
            font-size: 9pt;
            line-height: 1.2;
            color: black;
            background: white;
          }
          
          .no-print { 
            display: none !important; 
          }
          
          table {
            page-break-inside: auto;
            border-collapse: collapse;
            width: 100%;
            table-layout: fixed;
            font-size: 8pt;
          }
          
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          
          thead { 
            display: table-header-group; 
          }
          
          h1, h2, h3 { 
            page-break-after: avoid;
            color: black;
          }
          
          .page-break { 
            page-break-before: always; 
          }

          .summary-grid {
            display: block;
          }
          
          .summary-item {
            display: inline-block;
            width: 200px;
            margin-right: 10px;
            margin-bottom: 10px;
            border: 1px solid #ccc;
          }
        }
        
        @media screen {
          .print-preview {
            max-width: 8.5in;
            margin: 0 auto;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            padding: 1in;
          }
        }
      </style>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Report</title>
          <meta charset="utf-8">
          ${printCSS}
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;
  }

  /**
   * Open HTML content in new window
   */
  openHTMLInNewWindow(content) {
    const newWindow = window.open('', '_blank', 'width=1024,height=768');
    
    if (!newWindow) {
      throw new Error('Unable to open new window - popup may be blocked');
    }

    newWindow.document.write(content);
    newWindow.document.close();
    
    return newWindow;
  }

  /**
   * Download blob as file
   */
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Add export to history
   */
  addToHistory(exportRecord) {
    this.exportHistory.unshift(exportRecord);
    
    if (this.exportHistory.length > this.maxHistorySize) {
      this.exportHistory = this.exportHistory.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Get export history
   */
  getExportHistory(limit = 10) {
    return this.exportHistory.slice(0, limit);
  }

  /**
   * Clear export history
   */
  clearHistory() {
    this.exportHistory = [];
  }

  /**
   * Get supported export formats for a report type
   */
  getSupportedFormats(reportType) {
    // This would reference the report definition if available
    return [
      REPORT_FORMATS.HTML,
      REPORT_FORMATS.PDF,
      REPORT_FORMATS.CSV,
      REPORT_FORMATS.JSON
    ];
  }

  /**
   * Validate export request
   */
  validateExportRequest(content, reportType, format) {
    const errors = [];

    if (!content) {
      errors.push('Content is required for export');
    }

    if (!reportType) {
      errors.push('Report type is required for export');
    }

    const supportedFormats = this.getSupportedFormats(reportType);
    if (format && !supportedFormats.includes(format)) {
      errors.push(`Format ${format} is not supported for ${reportType}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

const exportService = new ExportService();
export default exportService;